import { toggleValues } from 'platform/site-wide/feature-toggles/selectors';
import {
  shaValidationAlertShown,
  trackShaFileUploadValidationResult,
  trackShaValidationErrorCorrected,
} from './tracking/bddShaTracking';

// Module-level map to track upload start times for duration calculation.
// Keyed by the File object reference — avoids collisions when the same file is uploaded twice.
// WeakMap so abandoned File objects (e.g. failed uploads) are GC'd automatically.
const uploadStartTimes = new WeakMap();

/**
 * Checks whether document validation is enabled from the options object
 * passed by the forms-system upload thunk.
 * @param {Object} options - Options object containing featureToggles
 * @param {Object} [options.featureToggles] - Feature toggle values
 * @returns {boolean}
 */
const isDocumentValidationEnabled = options => {
  if (!options?.featureToggles) return false;
  const toggles = toggleValues({ featureToggles: options.featureToggles });
  return !!toggles?.disability526DocumentValidationEnabled;
};

/**
 * Extracts the lowercase file extension from a file object.
 * @param {File} file
 * @returns {string}
 */
const getFileExtension = file => {
  const name = file?.name;
  if (typeof name !== 'string') return 'unknown';
  const lastDot = name.lastIndexOf('.');
  if (lastDot <= 0 || lastDot === name.length - 1) return 'unknown';
  return name.slice(lastDot + 1).toLowerCase();
};

/**
 * Creates an upload payload factory that optionally includes an attachment_id.
 * Attachment ID is only appended when document validation is enabled.
 * The 4th argument (options) is passed by the forms-system upload thunk.
 * @param {Object} options
 * @param {string} [options.attachmentId] - If provided, appends attachment_id to the payload
 * @returns {function(File, string, string, Object): FormData}
 */
export const createUploadPayload =
  ({ attachmentId } = {}) =>
  (file, _formId, password, options) => {
    uploadStartTimes.set(file, Date.now());
    const payload = new FormData();
    payload.append('supporting_evidence_attachment[file_data]', file);
    if (attachmentId && isDocumentValidationEnabled(options)) {
      payload.append(
        'supporting_evidence_attachment[attachment_id]',
        attachmentId,
      );
    }
    if (password) {
      payload.append('supporting_evidence_attachment[password]', password);
    }
    return payload;
  };

/**
 * Emits the SHA upload validation analytics for one completed upload: the
 * per-upload `21-526EZ_shaFileUploadValidationResult` event and, when a
 * previously-warned upload is replaced by a clean one, the correction event.
 *
 * Called from the v2 SHA upload page's own `parseResponse`, which must return the
 * raw `File` for `va-file-input-multiple` and so handles result-building itself.
 * Reads (and clears) the per-file start time recorded by `createUploadPayload` to
 * report duration. No-ops unless document validation is enabled.
 *
 * @param {Object} response - Parsed upload response body
 * @param {File} file - The uploaded file
 * @param {Object} [options] - Upload thunk options ({ featureToggles })
 * @param {string} [uploaderVersion] - Identifier for the uploader component
 */
export const trackShaUploadValidation = (
  response,
  file,
  options,
  uploaderVersion,
) => {
  // Compute upload duration from stored start time
  const startTime = uploadStartTimes.get(file);
  const uploadDurationMs =
    startTime != null ? Date.now() - startTime : undefined;
  uploadStartTimes.delete(file);

  if (!isDocumentValidationEnabled(options)) return;

  const rawWarnings = response?.data?.attributes?.warnings || [];
  // Normalize to string codes — API may return strings or objects with a .code property
  const warningCodes = rawWarnings
    .map(w => (typeof w === 'string' ? w : w?.code))
    .filter(Boolean);

  trackShaFileUploadValidationResult({
    hasValidationWarnings: warningCodes.length > 0,
    warningCodes,
    uploaderVersion,
    fileType: getFileExtension(file),
    uploadDurationMs,
  });

  // If this upload is clean and the user previously saw the validation alert,
  // track it as a correction (the latest document has no warnings).
  if (warningCodes.length === 0 && shaValidationAlertShown()) {
    trackShaValidationErrorCorrected({
      correctionType: 'post-error-matching-document-uploaded',
    });
  }
};
